import { bound } from "@frui.ts/helpers";
import { ConductorSingleChild, ScreenBase } from "@frui.ts/screens/dist";
import { interfaces } from "inversify";
import { computed, observable, reaction } from "mobx";
import LegalPerson from "models/legalPerson";
import NaturalPerson from "models/naturalPerson";
import Notice from "models/notice";
import LocalizationService from "services/localizationService";
import LegalPersonViewModel from "./legalPersonViewModel";
import NaturalPersonViewModel from "./naturalPersonViewModel";

export default class OperatorViewModel extends ConductorSingleChild<ScreenBase> {
  @observable legalPersonViewModel: LegalPersonViewModel;
  @observable naturalPersonViewModel: NaturalPersonViewModel;

  protected unsubscriptions: Array<() => void> = [];

  @computed
  get isRepresentedAsProxy() {
    return this.operator._isRepresentedByProxy;
  }

  set isRepresentedAsProxy(isRepresentedByProxy) {
    this.operator.isLegalPerson = false;
    const naturalPerson = (this.operator.naturalPerson = new NaturalPerson());
    naturalPerson.isRepresentedByProxy = isRepresentedByProxy;
    if (!isRepresentedByProxy) {
      naturalPerson.mailingAddress = this.notice.applicant.mailingAddress;
      naturalPerson.fullName = this.notice.applicant.fullName;
      naturalPerson.birthDate = this.notice.applicant.birthDate;
    } else {
      naturalPerson.mailingAddress = "";
      naturalPerson.fullName = "";
      naturalPerson.birthDate = undefined;
    }

    this.naturalPersonViewModel = this.naturalPersonInputVMFactory(naturalPerson);
    this.legalPersonViewModel = this.legalPersonInputVMFactory(this.operator.legalPerson || new LegalPerson());
    this.activateProperChildren();
  }

  set isLegalPerson(isLegal: boolean) {
    this.operator.isLegalPerson = isLegal;
    this.activateProperChildren();
  }

  @computed
  get isLegalPerson() {
    return this.operator.isLegalPerson;
  }

  @computed
  get operator() {
    return this.notice.vehicleOperator!;
  }

  constructor(
    public notice: Notice,
    private noticeType: "exemption" | "omission",
    private localizationService: LocalizationService,
    protected legalPersonInputVMFactory: ReturnType<typeof LegalPersonViewModel.Factory>,
    protected naturalPersonInputVMFactory: ReturnType<typeof NaturalPersonViewModel.Factory>,
    private persistNotice: () => void,
    public showBirthdate: boolean
  ) {
    super();
    this.naturalPersonViewModel = naturalPersonInputVMFactory(this.operator.naturalPerson || new NaturalPerson());
    this.legalPersonViewModel = legalPersonInputVMFactory(this.operator.legalPerson || new LegalPerson());
    this.unsubscriptions.push(
      reaction(
        () => [this.notice.applicant.fullName, this.notice.applicant.birthDate, this.notice.applicant.mailingAddress],
        this.onApplicantChange,
        { fireImmediately: true }
      ),
      reaction(
        () => [
          this.operator.naturalPerson?.fullName,
          this.operator.naturalPerson?.birthDate,
          this.operator.naturalPerson?.mailingAddress,
        ],
        this.persistNotice
      ),
      reaction(
        () => [
          this.operator.legalPerson?.companyName,
          this.operator.legalPerson?.companyRegistrationNumber,
          this.operator.legalPerson?.companyAddress,
        ],
        this.persistNotice
      ),
      reaction(() => this.operator.isLegalPerson, this.persistNotice)
    );
  }

  protected onInitialize(): Promise<any> | void {
    this.activateProperChildren();
    return super.onInitialize();
  }

  protected onDeactivate(isClosing: boolean) {
    if (isClosing) {
      this.unsubscriptions.forEach(unsubscribe => unsubscribe());
      this.unsubscriptions = [];
    }

    return super.onDeactivate(isClosing);
  }

  protected findNavigationChild(navigationName: string | undefined) {
    return this.naturalPersonViewModel;
  }

  activateProperChildren() {
    if (this.operator.isLegalPerson) {
      this.tryActivateChild(this.legalPersonViewModel);
    } else {
      this.tryActivateChild(this.naturalPersonViewModel);
    }
  }

  @bound
  onApplicantChange() {
    const naturalPerson = this.operator.naturalPerson;
    if (!this.isRepresentedAsProxy && naturalPerson) {
      naturalPerson.mailingAddress = this.notice.applicant.mailingAddress;
      naturalPerson.fullName = this.notice.applicant.fullName;
      naturalPerson.birthDate = this.notice.applicant.birthDate;
    }
    this.persistNotice();
  }

  @bound
  translate(code: string, size?: number) {
    return this.localizationService.translateGeneral(`${this.noticeType}.${code}`, size);
  }

  @bound
  translateGeneral(code: string, size?: number) {
    return this.localizationService.translateGeneral(code, size);
  }

  static Factory({ container }: interfaces.Context) {
    return (notice: Notice, noticeType: "exemption" | "omission", persistNotice: () => void, showBirthdate = true) =>
      new OperatorViewModel(
        notice,
        noticeType,
        container.get(LocalizationService),
        container.get(LegalPersonViewModel.Factory),
        container.get(NaturalPersonViewModel.Factory),
        persistNotice,
        showBirthdate
      );
  }
}
